import { Link, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { CheckCircle2, Phone, MessageSquare, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import AppointmentPicker from "@/components/AppointmentPicker";
import { BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED } from "@/lib/constants";
import { trackLead } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

export default function ThankYouPage() {
    const [params] = useSearchParams();
    const id = params.get("id");
    const service = params.get("service") || "Electricity";
    const channel = params.get("channel") || params.get("utm_source") || "";
    const name = params.get("name") || "";
    const phone = params.get("phone") || "";
    const email = params.get("email") || "";
    const { t } = useT();

    useEffect(() => {
        if (id) trackLead({ id, service, channel });
    }, [id, service, channel]);

    return (
        <div className="min-h-screen" data-testid="thank-you-page">
            <Navbar />
            <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 space-y-6">
                <div className="card-surface p-8 sm:p-10 text-center reveal">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                        <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                        {t("ty_title")}
                    </h1>
                    <p className="mt-3 text-slate-300 leading-relaxed">
                        {t("ty_sub")}
                    </p>
                    {id && (
                        <p className="mt-2 text-xs mono text-slate-500">
                            Request reference: {id}
                        </p>
                    )}
                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                        <a href={`tel:${BUSINESS_PHONE}`} className="btn-primary" data-testid="thanks-call-btn">
                            <Phone className="w-4 h-4" /> Call {BUSINESS_PHONE_FORMATTED}
                        </a>
                        <a href={`sms:${BUSINESS_PHONE}?&body=Hi, I just submitted a request for electricity options.`} className="btn-ghost" data-testid="thanks-text-btn">
                            <MessageSquare className="w-4 h-4" /> Text {BUSINESS_PHONE_FORMATTED}
                        </a>
                    </div>
                </div>

                {/* Appointment picker — keep momentum */}
                <AppointmentPicker leadId={id} defaultName={name} defaultPhone={phone} defaultEmail={email} />

                <div className="card-surface p-6 sm:p-8 reveal d1">
                    <h3 className="text-white font-semibold">{t("ty_next")}</h3>
                    <ol className="mt-3 space-y-2 text-sm text-slate-400 list-decimal list-inside">
                        <li>We review the details you shared.</li>
                        <li>A specialist contacts you using your preferred method.</li>
                        <li>We walk through available options - no pressure to enroll.</li>
                        <li>You decide on your own time.</li>
                    </ol>
                    <Link to="/" className="mt-6 inline-flex items-center gap-2 text-emerald-300 text-sm" data-testid="thanks-home-link">
                        Back to home <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
            <Footer />
            <StickyMobileCTA />
        </div>
    );
}
